/*
 * Vehicle History Component - Traccar-like functionality
 *
 * Features:
 * - Historical route tracking for private vehicles only
 * - Single date selection with time range filtering
 * - Interactive map with route visualization
 * - Location timeline with timestamps
 * - Statistics dashboard (distance, speed, active time)
 * - Quick date/time presets (Today, Yesterday, Dawn, Morning, Afternoon, Evening)
 * - Export functionality
 *
 * PRIVATE VEHICLES ONLY - No access for public vehicles or buses
 */

import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.Default.css';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { API, APP_CONSTANTS, getTimeSinceUpdate, MARKERS, getVehicleMarkerIcon } from '../../utils/config';
import MapController from '../Map/MapController';

/**
 * Convert heading degrees to compass direction
 * @param {number} degrees - Heading in degrees (0-360)
 * @returns {string} Compass direction (N, NE, E, SE, S, SW, W, NW)
 */
const getCompassDirection = (degrees) => {
  if (!degrees && degrees !== 0) return '';

  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round((degrees % 360) / 45) % 8;
  return directions[index];
};

/**
 * Convert km/h to mph
 * @param {number} kmh - Speed in kilometers per hour
 * @returns {number} Speed in miles per hour (rounded to 1 decimal place)
 */
const kmhToMph = (kmh) => {
  if (!kmh) return 0;
  return Math.round(kmh * 0.621371 * 10) / 10;
};

/**
 * Convert km to miles
 * @param {number} km - Distance in kilometers
 * @returns {number} Distance in miles (rounded to 1 decimal place)
 */
const kmToMiles = (km) => {
  if (!km) return 0;
  return Math.round(km * 0.621371 * 10) / 10;
};

/**
 * Get current date in UTC-6 (Belize Time) timezone
 * @returns {string} Current date in YYYY-MM-DD format in UTC-6
 */
const getCurrentDateInUTCMinus6 = () => {
  const now = new Date();
  // Get UTC time in milliseconds and subtract 6 hours (6 * 60 * 60 * 1000)
  const utcMinus6Time = new Date(now.getTime() - (6 * 60 * 60 * 1000));
  return utcMinus6Time.toISOString().split('T')[0];
};

/**
 * Format timestamp with proper UTC handling
 * @param {string} timestamp - UTC timestamp string
 * @returns {string} Formatted local time string
 */
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown time';

  let dateTime;
  if (typeof timestamp === 'string') {
    // Handle ISO timestamp strings properly
    if (timestamp.endsWith('Z')) {
      dateTime = new Date(timestamp);
    } else if (timestamp.includes('T')) {
      // ISO string without timezone - assume UTC
      dateTime = new Date(timestamp + 'Z');
    } else {
      // Fallback for other formats
      dateTime = new Date(timestamp);
    }
  } else {
    // Handle Date objects
    dateTime = new Date(timestamp);
  }

  // Check if date is valid
  if (isNaN(dateTime.getTime())) {
    return 'Invalid time';
  }

  return dateTime.toLocaleString();
};

/**
 * Date Picker Component - Single date with time range
 */
const DateRangePicker = ({ selectedDate, startTime, endTime, onDateChange, onStartTimeChange, onEndTimeChange, onApply, loading }) => {
  const today = getCurrentDateInUTCMinus6();
  const yesterday = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  })();

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <h3 className="font-semibold text-gray-900 mb-4">📅 Select Date & Time (UTC-6)</h3>

      {/* Date and Time Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date
          </label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => onDateChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            max={today}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Time
          </label>
          <input
            type="time"
            value={startTime}
            onChange={(e) => onStartTimeChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Time
          </label>
          <input
            type="time"
            value={endTime}
            onChange={(e) => onEndTimeChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={onApply}
            disabled={loading}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Loading...' : 'Apply Filter'}
          </button>
        </div>
      </div>

      {/* Quick Date Buttons */}
      <div className="flex flex-wrap gap-2 mb-3">
        <span className="text-sm text-gray-600 mr-2">Quick Date:</span>
        <button
          onClick={() => onDateChange(today)}
          className={`px-3 py-1 text-sm rounded-md border transition-colors ${
            selectedDate === today
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'
          }`}
        >
          Today
        </button>
        <button
          onClick={() => onDateChange(yesterday)}
          className={`px-3 py-1 text-sm rounded-md border transition-colors ${
            selectedDate === yesterday
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'
          }`}
        >
          Yesterday
        </button>
      </div>

      {/* Quick Time Buttons */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-600 mr-2">Quick Time:</span>
        <button
          onClick={() => { onStartTimeChange('00:00'); onEndTimeChange('23:59'); }}
          className={`px-3 py-1 text-sm rounded-md border transition-colors ${
            startTime === '00:00' && endTime === '23:59'
              ? 'bg-green-600 text-white border-green-600'
              : 'bg-white text-gray-600 border-gray-400 hover:bg-gray-50'
          }`}
        >
          All Day
        </button>
        <button
          onClick={() => { onStartTimeChange('03:00'); onEndTimeChange('06:00'); }}
          className={`px-3 py-1 text-sm rounded-md border transition-colors ${
            startTime === '03:00' && endTime === '06:00'
              ? 'bg-green-600 text-white border-green-600'
              : 'bg-white text-gray-600 border-gray-400 hover:bg-gray-50'
          }`}
        >
          Dawn
        </button>
        <button
          onClick={() => { onStartTimeChange('06:00'); onEndTimeChange('12:00'); }}
          className={`px-3 py-1 text-sm rounded-md border transition-colors ${
            startTime === '06:00' && endTime === '12:00'
              ? 'bg-green-600 text-white border-green-600'
              : 'bg-white text-gray-600 border-gray-400 hover:bg-gray-50'
          }`}
        >
          Morning
        </button>
        <button
          onClick={() => { onStartTimeChange('12:00'); onEndTimeChange('18:00'); }}
          className={`px-3 py-1 text-sm rounded-md border transition-colors ${
            startTime === '12:00' && endTime === '18:00'
              ? 'bg-green-600 text-white border-green-600'
              : 'bg-white text-gray-600 border-gray-400 hover:bg-gray-50'
          }`}
        >
          Afternoon
        </button>
        <button
          onClick={() => { onStartTimeChange('18:00'); onEndTimeChange('23:59'); }}
          className={`px-3 py-1 text-sm rounded-md border transition-colors ${
            startTime === '18:00' && endTime === '23:59'
              ? 'bg-green-600 text-white border-green-600'
              : 'bg-white text-gray-600 border-gray-400 hover:bg-gray-50'
          }`}
        >
          Evening
        </button>
      </div>
    </div>
  );
};

/**
 * Statistics Dashboard Component
 */
const StatisticsDashboard = ({ statistics, vehicleInfo }) => {
  if (!statistics) return null;

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center">
        <span className="mr-2">📊</span>
        Vehicle Statistics
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-blue-600 text-2xl mr-3">📍</div>
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Locations</p>
              <p className="text-2xl font-bold text-blue-800">{statistics.total_locations || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-green-600 text-2xl mr-3">🛣️</div>
            <div>
              <p className="text-sm text-green-600 font-medium">Distance Traveled</p>
              <p className="text-2xl font-bold text-green-800">{kmToMiles(statistics.total_distance_km)} mi</p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-purple-600 text-2xl mr-3">🚀</div>
            <div>
              <p className="text-sm text-purple-600 font-medium">Max Speed</p>
              <p className="text-2xl font-bold text-purple-800">{kmhToMph(statistics.max_speed_kmh)} mph</p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-orange-600 text-2xl mr-3">⏱️</div>
            <div>
              <p className="text-sm text-orange-600 font-medium">Active Time</p>
              <p className="text-2xl font-bold text-orange-800">
                {statistics.time_range ? `${statistics.time_range.duration_hours.toFixed(1)}h` : '0h'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Information */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="font-medium text-gray-900 mb-2">Vehicle Information</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Make & Model</p>
            <p className="font-medium">{vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}</p>
          </div>
          <div>
            <p className="text-gray-600">License Plate</p>
            <p className="font-medium">{vehicleInfo.license_plate}</p>
          </div>
          <div>
            <p className="text-gray-600">Color</p>
            <p className="font-medium">{vehicleInfo.color}</p>
          </div>
          <div>
            <p className="text-gray-600">Type</p>
            <p className="font-medium">Private Vehicle</p>
          </div>
          <div>
            <p className="text-gray-600">History Period</p>
            <p className="font-medium">
              {statistics.time_range ?
                `${formatTimestamp(statistics.time_range.start).split(',')[0]} - ${formatTimestamp(statistics.time_range.end).split(',')[0]}`
                : 'No data'
              }
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

/**
 * Location Timeline Component - Enhanced with clickable records
 */
const LocationTimeline = ({ locations, onLocationClick, selectedLocation }) => {
  if (!locations || locations.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-bold text-lg text-gray-900 mb-4">📋 Location Timeline</h3>
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-4">📍</div>
          <p className="text-gray-600">No location data available for the selected period</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center">
        <span className="mr-2">📋</span>
        Location Timeline ({locations.length} points)
      </h3>

      <div className="max-h-96 overflow-y-auto">
        <div className="space-y-3">
          {locations.slice(0, 50).map((location, index) => (
            <div
              key={location.id || index}
              className={`flex items-start space-x-3 p-3 rounded-lg cursor-pointer transition-colors hover:bg-blue-50 ${selectedLocation?.id === location.id ? 'bg-blue-100 border-2 border-blue-300' : 'bg-gray-50'
                }`}
              onClick={() => onLocationClick(location)}
            >
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${selectedLocation?.id === location.id ? 'bg-blue-600 text-white' : 'bg-gray-600 text-white'
                  }`}>
                  {index + 1}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      📍 {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                    </p>
                    <p className="text-xs text-gray-600">
                      {formatTimestamp(location.timestamp)}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      👆 Click to view on map
                    </p>
                  </div>
                  <div className="text-right">
                    {location.speed && (
                      <p className="text-xs text-gray-600">
                        🚀 {kmhToMph(location.speed)} mph
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      {getTimeSinceUpdate(location.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {locations.length > 50 && (
            <div className="text-center py-4">
              <p className="text-sm text-gray-600">
                Showing first 50 locations. Total: {locations.length}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Identify route starts and ends based on 15-minute gaps
 * Route Start: First location OR any location that comes after a 15+ minute gap
 * Route End: Last location OR any location that is followed by a 15+ minute gap
 * This helps identify actual trips/journeys rather than just chronological endpoints
 * @param {Array} locations - Array of location objects (reverse chronological order)
 * @returns {Object} Object with routeStarts and routeEnds arrays
 */
const identifyRouteSegments = (locations) => {
  if (!locations || locations.length === 0) {
    return { routeStarts: [], routeEnds: [] };
  }

  // Sort locations chronologically (oldest first) for easier processing
  const sortedLocations = [...locations].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const routeStarts = [];
  const routeEnds = [];
  const FIFTEEN_MINUTES_MS = 15 * 60 * 1000; // 15 minutes in milliseconds

  for (let i = 0; i < sortedLocations.length; i++) {
    const currentLocation = sortedLocations[i];
    const currentTime = new Date(currentLocation.timestamp);

    // Check for route start (gap before this location)
    if (i === 0) {
      // First location is always a route start
      routeStarts.push(currentLocation);
    } else {
      const previousTime = new Date(sortedLocations[i - 1].timestamp);
      const timeDiff = currentTime - previousTime;

      if (timeDiff > FIFTEEN_MINUTES_MS) {
        routeStarts.push(currentLocation);
      }
    }

    // Check for route end (gap after this location)
    if (i === sortedLocations.length - 1) {
      // Last location is always a route end
      routeEnds.push(currentLocation);
    } else {
      const nextTime = new Date(sortedLocations[i + 1].timestamp);
      const timeDiff = nextTime - currentTime;

      if (timeDiff > FIFTEEN_MINUTES_MS) {
        routeEnds.push(currentLocation);
      }
    }
  }

  return { routeStarts, routeEnds };
};

/**
 * Pre-compute headings for all locations to avoid recalculating on each render
 */
const computeLocationHeadings = (locations) => {
  if (!locations || locations.length === 0) return new Map();

  // Sort chronologically once
  const sortedLocations = [...locations].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const headingMap = new Map();

  for (let i = 0; i < sortedLocations.length; i++) {
    const current = sortedLocations[i];
    let rotation = current.heading || 0;

    // Calculate bearing if no heading and there's a next point
    if (!current.heading && i < sortedLocations.length - 1) {
      const next = sortedLocations[i + 1];
      const lat1 = current.latitude * Math.PI / 180;
      const lat2 = next.latitude * Math.PI / 180;
      const deltaLng = (next.longitude - current.longitude) * Math.PI / 180;
      const y = Math.sin(deltaLng) * Math.cos(lat2);
      const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);
      rotation = (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
    }

    headingMap.set(current.id || `${current.latitude}-${current.longitude}-${current.timestamp}`, rotation);
  }

  return headingMap;
};

/**
 * Create GPS point icon with pre-computed heading
 */
const createGPSPointIcon = (rotation, isSelected = false) => {
  const color = isSelected ? '#ef4444' : '#3b82f6';
  const strokeColor = isSelected ? '#dc2626' : '#1d4ed8';

  const arrowSvg = `
    <svg width="16" height="16" viewBox="0 0 16 16" style="transform: rotate(${rotation}deg); filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3)); cursor: pointer;">
      <polygon points="6,2 9,7 6,6 3,7" fill="${color}" stroke="${strokeColor}" stroke-width="0.5"/>
    </svg>
  `;

  return L.divIcon({
    html: arrowSvg,
    className: 'custom-gps-arrow',
    iconSize: [12, 12],
    iconAnchor: [6, 6]
  });
};

/**
 * Component to track map bounds and filter visible markers
 */
const ViewportMarkers = ({ locations, headingMap, selectedLocation, vehicleInfo }) => {
  const map = useMap();
  const [visibleLocations, setVisibleLocations] = useState([]);

  useEffect(() => {
    const updateVisibleMarkers = () => {
      const bounds = map.getBounds();
      const zoom = map.getZoom();

      // Filter locations within current bounds with padding
      const padded = bounds.pad(0.1); // 10% padding
      let filtered = locations.filter(loc =>
        padded.contains([loc.latitude, loc.longitude])
      );

      // Limit markers based on zoom level for better performance
      // At low zoom, show fewer points; at high zoom, show all
      const maxMarkers = zoom < 12 ? 100 : zoom < 14 ? 300 : zoom < 16 ? 800 : filtered.length;

      if (filtered.length > maxMarkers) {
        // Sample evenly across the filtered set
        const step = Math.ceil(filtered.length / maxMarkers);
        filtered = filtered.filter((_, i) => i % step === 0);
      }

      setVisibleLocations(filtered);
    };

    // Initial update
    updateVisibleMarkers();

    // Update on map events
    map.on('moveend', updateVisibleMarkers);
    map.on('zoomend', updateVisibleMarkers);

    return () => {
      map.off('moveend', updateVisibleMarkers);
      map.off('zoomend', updateVisibleMarkers);
    };
  }, [map, locations]);

  return (
    <MarkerClusterGroup
      chunkedLoading
      maxClusterRadius={80}
      spiderfyOnMaxZoom={true}
      showCoverageOnHover={false}
      zoomToBoundsOnClick={true}
      disableClusteringAtZoom={16}
      removeOutsideVisibleBounds={true}
      animate={false}
      chunkInterval={100}
      chunkDelay={50}
    >
      {visibleLocations.map((location) => {
        const isSelected = selectedLocation?.id === location.id;
        const locKey = location.id || `${location.latitude}-${location.longitude}-${location.timestamp}`;
        const rotation = headingMap.get(locKey) || 0;

        return (
          <Marker
            key={locKey}
            position={[location.latitude, location.longitude]}
            icon={createGPSPointIcon(rotation, isSelected)}
          >
            <Popup>
              <div className="text-sm">
                <h4 className={`font-bold mb-2 ${isSelected ? 'text-red-600' : 'text-blue-600'}`}>
                  📍 GPS Point
                </h4>
                <p><strong>Vehicle:</strong> {vehicleInfo.make} {vehicleInfo.model}</p>
                <p><strong>Time:</strong> {formatTimestamp(location.timestamp)}</p>
                <p><strong>Location:</strong> {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}</p>
                {typeof location.speed === "number" && (
                  <p><strong>Speed:</strong> {kmhToMph(location.speed)} mph</p>
                )}
                {location.heading && (
                  <p><strong>Direction:</strong> {location.heading}° {getCompassDirection(location.heading)}</p>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MarkerClusterGroup>
  );
};

/**
 * Get icon based on vehicle type
 */
const getIconForVehicleType = (vehicleType, isSelected = false) => {
  switch (vehicleType?.toLowerCase()) {
    case 'bus':
      return MARKERS.busIcon(isSelected);
    case 'truck':
    case 'delivery':
      return MARKERS.truckIcon(isSelected);
    case 'van':
      return MARKERS.vanIcon(isSelected);
    case 'taxi':
      return MARKERS.taxiIcon(isSelected);
    default:
      return MARKERS.privateVehicleIcon(isSelected);
  }
};

/**
 * History Map Component - Enhanced with GPS point indicators
 */
const HistoryMap = ({ locations, vehicleInfo, vehicleType, selectedLocation, onLocationSelect, isFullscreen, setIsFullscreen, mapRef, onSaveImage, savingImage }) => {
  // Pre-compute headings once when locations change
  const headingMap = React.useMemo(() => computeLocationHeadings(locations), [locations]);

  if (!locations || locations.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-bold text-lg text-gray-900 mb-4">🗺️ Route Map</h3>
        <div className="h-96 bg-gray-50 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-400 text-4xl mb-4">🗺️</div>
            <p className="text-gray-600">No route data to display</p>
          </div>
        </div>
      </div>
    );
  }

  // Prepare route coordinates for polyline
  const routeCoordinates = locations
    .filter(loc => loc.latitude && loc.longitude)
    .map(loc => [loc.latitude, loc.longitude])
    .reverse(); // Reverse to show chronological order

  // Identify route segments based on 15-minute gaps
  const { routeStarts, routeEnds } = identifyRouteSegments(locations);

  // Calculate map center - use selected location or calculate from all locations
  let mapCenter;
  if (selectedLocation) {
    mapCenter = [selectedLocation.latitude, selectedLocation.longitude];
  } else {
    const centerLat = routeCoordinates.reduce((sum, coord) => sum + coord[0], 0) / routeCoordinates.length;
    const centerLng = routeCoordinates.reduce((sum, coord) => sum + coord[1], 0) / routeCoordinates.length;
    mapCenter = [centerLat, centerLng];
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-lg text-gray-900 flex items-center">
          <span className="mr-2">🗺️</span>
          Route Map
        </h3>
        <div className="text-sm text-gray-600 text-right">
          <div>{routeCoordinates.length} location points</div>
          <div className="text-xs mt-1">
            {routeStarts.length} route{routeStarts.length !== 1 ? 's' : ''} •
            {routeEnds.length} stop{routeEnds.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <div className={`rounded-lg overflow-hidden shadow-lg relative ${isFullscreen ? 'h-screen' : ''}`} style={isFullscreen ? {} : { height: '500px' }}>
        <MapContainer
          ref={mapRef}
          center={mapCenter}
          zoom={13}// zoom={selectedLocation ? 16 : 13}
          style={{ height: '100%', width: '100%' }}
          preferCanvas={true} // Render vector layers (route line) to canvas so image capture doesn't shift them
        // key={selectedLocation?.id || 'default'} // Force re-render when selectedLocation changes
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          // attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          {selectedLocation && (
            <MapController center={mapCenter} zoom={16} />
          )}

          {/* Route polyline  */}
          {routeCoordinates.length > 1 && (
            <Polyline
              positions={routeCoordinates}
              color="blue"
              weight={3}
              opacity={0.7}
            />
          )}

          {/* GPS Point markers - viewport-based filtering with clustering */}
          <ViewportMarkers
            locations={locations}
            headingMap={headingMap}
            selectedLocation={selectedLocation}
            vehicleInfo={vehicleInfo}
          />

          {/* Route Start markers - UPDATED: Multiple starts based on 15-minute gaps */}
          {routeStarts.map((startLocation, index) => (
            <Marker
              key={`start-${startLocation.id || index}`}
              position={[startLocation.latitude, startLocation.longitude]}
              icon={getIconForVehicleType(vehicleType, false)}
            >
              <Popup>
                <div className="text-sm">
                  <h4 className="font-bold text-green-600 mb-2">🟢 Route Start #{index + 1}</h4>
                  <p><strong>Vehicle:</strong> {vehicleInfo.make} {vehicleInfo.model}</p>
                  <p><strong>Time:</strong> {formatTimestamp(startLocation.timestamp)}</p>
                  <p><strong>Location:</strong> {startLocation.latitude.toFixed(6)}, {startLocation.longitude.toFixed(6)}</p>
                  {/* {startLocation.speed && (
                    <p><strong>Speed:</strong> {kmhToMph(startLocation.speed)} mph</p>
                  )} */}
                  {typeof startLocation.speed === "number" && (
                    <p><strong>Speed:</strong> {kmhToMph(startLocation.speed)} mph</p>
                  )}
                  <div className="mt-2 pt-2 border-t border-green-200">
                    <p className="text-xs text-green-600">
                      {index === 0 ? '🚀 First location recorded' : '⏰ Activity resumed after 15+ minute gap'}
                    </p>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Route End markers - UPDATED: Multiple ends based on 15-minute gaps */}
          {routeEnds.map((endLocation, index) => (
            <Marker
              key={`end-${endLocation.id || index}`}
              position={[endLocation.latitude, endLocation.longitude]}
              icon={getIconForVehicleType(vehicleType, true)}
            >
              <Popup>
                <div className="text-sm">
                  <h4 className="font-bold text-red-600 mb-2">🔴 Route End #{index + 1}</h4>
                  <p><strong>Vehicle:</strong> {vehicleInfo.make} {vehicleInfo.model}</p>
                  <p><strong>Time:</strong> {formatTimestamp(endLocation.timestamp)}</p>
                  <p><strong>Location:</strong> {endLocation.latitude.toFixed(6)}, {endLocation.longitude.toFixed(6)}</p>
                  {/* {endLocation.speed && (
                    <p><strong>Speed:</strong> {kmhToMph(endLocation.speed)} mph</p>
                  )} */}
                  {typeof endLocation.speed === "number" && (
                    <p><strong>Speed:</strong> {kmhToMph(endLocation.speed)} mph</p>
                  )}
                  <div className="mt-2 pt-2 border-t border-red-200">
                    <p className="text-xs text-red-600">
                      {index === routeEnds.length - 1 ? '🏁 Last location recorded' : '⏸️ Activity paused - 15+ minute gap follows'}
                    </p>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
        {/* Save as Image Button */}
        {onSaveImage && (
          <button
            onClick={onSaveImage}
            disabled={savingImage}
            className="absolute top-2 right-12 z-[1000] bg-white hover:bg-gray-50 border border-gray-300 rounded-md p-2 shadow-lg transition-colors duration-200 disabled:opacity-50"
            title="Save map and vehicle details as image"
            aria-label="Save map as image"
          >
            {savingImage ? (
              <span className="block w-4 h-4 text-xs leading-4 text-gray-700">⏳</span>
            ) : (
              <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 019.07 4h5.86a2 2 0 011.664.89l.812 1.22A2 2 0 0019.07 7H20a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        )}
        {/* Fullscreen Toggle Button */}
        <button
          onClick={() => setIsFullscreen(!isFullscreen)}
          className="absolute top-2 right-2 z-[1000] bg-white hover:bg-gray-50 border border-gray-300 rounded-md p-2 shadow-lg transition-colors duration-200"
          title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isFullscreen ? (
            // Exit fullscreen icon (compress/minimize)
            <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          ) : (
            // Enter fullscreen icon (expand)
            <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8l4-4m0 0h4M8 4v4m8-4l4 4m0 0v4m0-4h-4m4 16l-4-4m0 0v-4m0 4h-4m-4 4v-4m0 0H4m4 0l-4-4" />
            </svg>
          )}
        </button>
      </div>

      {/* Map Legend */}
      {!isFullscreen && (
        <div className="mt-4">
          <div className="flex justify-center space-x-6 text-sm mb-3">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
              <span>Route Starts ({routeStarts.length})</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-1 bg-blue-500 mr-2"></div>
              <span>Vehicle Route</span>
            </div>
            <div className="flex items-center">
              <svg width="12" height="12" viewBox="0 0 12 12" className="mr-2">
                <polygon points="6,2 9,7 6,6 3,7" fill="#3b82f6" stroke="#1d4ed8" strokeWidth="0.5" />
              </svg>
              <span>Direction Arrows</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-red-500 rounded-full mr-2"></div>
              <span>Route Ends ({routeEnds.length})</span>
            </div>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600 bg-gray-50 rounded px-3 py-2 inline-block">
              🟢 <strong>Route Starts:</strong> First location or after 15+ minute gap •
              🔴 <strong>Route Ends:</strong> Last location or before 15+ minute gap •
              ▲ <strong>Arrows:</strong> Show direction of travel from GPS heading or calculated bearing
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Main Vehicle History Component - Enhanced with location selection
 */
const VehicleHistory = ({ vehicle, onClose }) => {
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('map');
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [savingImage, setSavingImage] = useState(false);
  const mapRef = useRef();

  // Single date state - using UTC-6 (Belize Time)
  const [selectedDate, setSelectedDate] = useState(() => {
    return getCurrentDateInUTCMinus6(); // Default to today in UTC-6
  });

  // Time range state (UTC-6)
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');

  // Load initial data
  useEffect(() => {
    if (vehicle) {
      loadHistoryData();
    }
  }, [vehicle]);

  // Handle map resize when entering/exiting fullscreen
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapRef.current) {
        const map = mapRef.current;
        if (map && map.invalidateSize) {
          map.invalidateSize();
        }
      }
    }, 100); // Small delay to ensure CSS transition completes

    return () => clearTimeout(timer);
  }, [isFullscreen]);

  // Add keyboard support for exiting fullscreen
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  const loadHistoryData = async () => {
    setLoading(true);
    setError('');

    try {
      // Construct datetime strings with UTC-6 timezone offset
      // Format: YYYY-MM-DDTHH:MM:SS-06:00
      const startDateTime = `${selectedDate}T${startTime}:00-06:00`;
      const endDateTime = `${selectedDate}T${endTime}:59-06:00`;

      const response = await axios.get(`${API}/vehicles/${vehicle.id}/history`, {
        params: {
          start_date: startDateTime,
          end_date: endDateTime,
          limit: 50000 // Higher limit since we're only loading one day (optimized view handles large datasets)
        }
      });

      setHistoryData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setError('Access denied. Vehicle history not available.');
      } else if (error.response?.status === 404) {
        setError('Vehicle not found.');
      } else {
        setError('Failed to load vehicle history. Please try again.');
      }
    }

    setLoading(false);
  };

  const handleApplyDateFilter = () => {
    loadHistoryData();
  };

  const handleLocationClick = (location) => {
    setSelectedLocation(location);
    setActiveTab('map'); // Switch to map tab to show the selected location
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const handleExportData = () => {
    if (!historyData?.locations) return;

    // Create CSV data
    const csvHeader = 'Timestamp,Latitude,Longitude,Speed (mph),Heading\n';
    const csvData = historyData.locations.map(loc =>
      `${loc.timestamp},${loc.latitude},${loc.longitude},${loc.speed ? kmhToMph(loc.speed) : ''},${loc.heading || ''}`
    ).join('\n');

    // Download CSV file
    const blob = new Blob([csvHeader + csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const timeRange = startTime !== '00:00' || endTime !== '23:59'
      ? `_${startTime.replace(':', '')}-${endTime.replace(':', '')}`
      : '';
    a.download = `vehicle_history_${vehicle.license_plate}_${selectedDate}${timeRange}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Capture the displayed map + vehicle details and download as a JPEG
  const handleExportImage = async () => {
    const map = mapRef.current;
    if (!map || !map.getContainer) {
      setError('Open the Route Map tab to save it as an image.');
      return;
    }

    setSavingImage(true);
    try {
      // Close any open popup so it doesn't cover the route
      map.closePopup();

      // Capture the visible map (tiles, route line, markers)
      const mapCanvas = await html2canvas(map.getContainer(), {
        useCORS: true,
        logging: false,
        scale: 2,
        onclone: (clonedDoc) => {
          // Leave the route line unobstructed in the image: drop the cluster
          // circles and the large vehicle markers (capture only, map unchanged)
          clonedDoc
            .querySelectorAll('.marker-cluster, .custom-vehicle-icon')
            .forEach(el => el.remove());
        }
      });

      // Compose the final image: white header card with vehicle details, then the map
      const s = 2; // same scale factor as the capture, keeps text crisp
      const pad = 16 * s;
      const headerHeight = 118 * s;
      const footerHeight = 26 * s;
      const canvas = document.createElement('canvas');
      canvas.width = mapCanvas.width;
      canvas.height = headerHeight + mapCanvas.height + footerHeight;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const stats = historyData?.statistics || {};
      const title = `Vehicle History — ${[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}`;

      ctx.fillStyle = '#111827';
      ctx.font = `bold ${17 * s}px Arial, sans-serif`;
      ctx.fillText(title, pad, 28 * s);

      ctx.font = `${12 * s}px Arial, sans-serif`;
      ctx.fillStyle = '#374151';
      ctx.fillText(
        `License plate: ${vehicle.license_plate || 'N/A'}`,
        pad, 52 * s
      );
      ctx.fillText(
        `Date: ${selectedDate}   •   Time: ${startTime} – ${endTime} (UTC-6)`,
        pad, 72 * s
      );
      const duration = stats.time_range ? `${stats.time_range.duration_hours.toFixed(1)}h` : '0h';
      ctx.fillText(
        `Locations: ${stats.total_locations || 0}   •   Distance: ${kmToMiles(stats.total_distance_km)} mi   •   ` +
        `Max speed: ${kmhToMph(stats.max_speed_kmh)} mph   •   Active time: ${duration}`,
        pad, 92 * s
      );

      // Divider between header and map
      ctx.strokeStyle = '#E5E7EB';
      ctx.lineWidth = s;
      ctx.beginPath();
      ctx.moveTo(0, headerHeight - s);
      ctx.lineTo(canvas.width, headerHeight - s);
      ctx.stroke();

      ctx.drawImage(mapCanvas, 0, headerHeight);

      // Footer with generation time and map attribution
      ctx.fillStyle = '#6B7280';
      ctx.font = `${9 * s}px Arial, sans-serif`;
      ctx.fillText(
        `Generated ${new Date().toLocaleString()}   •   Map © OpenStreetMap contributors`,
        pad, headerHeight + mapCanvas.height + 17 * s
      );

      // Download as JPEG
      const timeRange = startTime !== '00:00' || endTime !== '23:59'
        ? `_${startTime.replace(':', '')}-${endTime.replace(':', '')}`
        : '';
      canvas.toBlob((blob) => {
        if (!blob) {
          setError('Could not generate the image. Please try again.');
          return;
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vehicle_history_${vehicle.license_plate}_${selectedDate}${timeRange}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 'image/jpeg', 0.92);
    } catch (err) {
      setError('Could not capture the map image. Please try again.');
    } finally {
      setSavingImage(false);
    }
  };

  if (!vehicle) return null;

  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-[10000] bg-white">
        {/* Fullscreen content - only the map */}
        <div className="h-full w-full">
          {historyData && !loading && activeTab === 'map' && (
            <HistoryMap
              locations={historyData.locations}
              vehicleInfo={historyData.vehicle_info}
              vehicleType={vehicle?.vehicle_type}
              selectedLocation={selectedLocation}
              onLocationSelect={handleLocationSelect}
              isFullscreen={isFullscreen}
              setIsFullscreen={setIsFullscreen}
              mapRef={mapRef}
              onSaveImage={handleExportImage}
              savingImage={savingImage}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg mx-4 overflow-y-auto max-w-6xl w-full max-h-[90vh]">
        {/* Header */}
        {!isFullscreen && (
          <div className="bg-white p-6 border-b border-gray-200 flex-shrink-0">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-1xl font-bold text-gray-900 flex items-center">
                  <span className="mr-3">📊</span>
                  History - {vehicle.year} {vehicle.make} {vehicle.model}
                </h3>
                {/* <p className="text-gray-600 mt-1">
                License: {vehicle.license_plate} • {vehicle.is_public ? 'Public Vehicle' : 'Private Vehicle'}
              </p> */}
              </div>
              <div className="flex items-center space-x-2">
                {historyData?.locations && activeTab === 'map' && (
                  <button
                    onClick={handleExportImage}
                    disabled={savingImage}
                    className="flex items-center px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    title="Save the map and vehicle details as a JPEG image"
                  >
                    <span className="mr-2">🖼️</span>
                    {savingImage ? 'Saving...' : 'Image'}
                  </button>
                )}
                {historyData?.locations && (
                  <button
                    onClick={handleExportData}
                    className="flex items-center px-2 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    <span className="mr-2">📥</span>
                    CSV
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">{/* This ensures the content area is scrollable */}
          {/* Date Range Picker */}
          {!isFullscreen && (
            <DateRangePicker
              selectedDate={selectedDate}
              startTime={startTime}
              endTime={endTime}
              onDateChange={setSelectedDate}
              onStartTimeChange={setStartTime}
              onEndTimeChange={setEndTime}
              onApply={handleApplyDateFilter}
              loading={loading}
            />
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">⏳</div>
              <p className="text-gray-600">Loading vehicle history...</p>
            </div>
          )}

          {/* History Data */}
          {historyData && !loading && (
            <>
              {/* Statistics Dashboard */}
              {!isFullscreen && (
                <StatisticsDashboard
                  statistics={historyData.statistics}
                  vehicleInfo={historyData.vehicle_info}
                />
              )}

              {/* Tab Navigation */}
              {!isFullscreen && (
                <div className="bg-white rounded-lg shadow mb-6">
                  <div className="border-b border-gray-200">
                    <nav className="flex space-x-8 px-6">
                      {[
                        { id: 'map', name: 'Route Map', icon: '🗺️' },
                        { id: 'timeline', name: 'Location Timeline', icon: '📋' }
                      ].map((tab) => (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                          <span className="mr-2">{tab.icon}</span>
                          {tab.name}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              )}

              {/* Tab Content */}
              {activeTab === 'map' && (
                <HistoryMap
                  locations={historyData.locations}
                  vehicleInfo={historyData.vehicle_info}
                  vehicleType={vehicle?.vehicle_type}
                  selectedLocation={selectedLocation}
                  onLocationSelect={handleLocationSelect}
                  isFullscreen={isFullscreen}
                  setIsFullscreen={setIsFullscreen}
                  mapRef={mapRef}
                  onSaveImage={handleExportImage}
                  savingImage={savingImage}
                />
              )}

              {!isFullscreen && activeTab === 'timeline' && (
                <LocationTimeline
                  locations={historyData.locations}
                  onLocationClick={handleLocationClick}
                  selectedLocation={selectedLocation}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleHistory;